import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Navbar from "../../components/layout/Navbar";
import Footer from "../../components/layout/Footer";
import Toast from "../../components/ui/Toast";
// TextInput import removed — legacy email/phone form has been replaced by the
// Payment Gateway picker. Re-add `import TextInput from "../../components/formInputs/TextInput";`
// if you uncomment the legacy form block below.
import { fetchTvStoreProduct } from "../../redux/content";
import { purchasePackage } from "../../redux/subscription";
import { getClientApiUrl } from "../../config/apiConfig";
import "../../components/styles/pickPlanPage.scss";

/**
 * PickPlanPage — Subscription plan selection screen
 * Figma: Subscriptions (Discovery) Pay (25460:28674)
 *
 * Data flow:
 *   1. Get productId from URL param
 *   2. Fetch TV Store product detail → get price_options (plans)
 *   3. User selects a plan → "Start {Plan} Pass" button
 *   4. On click → Payment Gateway selector modal opens, user picks a gateway
 *
 * Layout:
 *   Left: "Pick a Plan" heading + description
 *   Right: Plan radio cards + CTA button
 */

// Payment gateways shown in the picker modal. `id` is passed through to the
// CRM purchase call so the backend can route to the chosen provider.
//
// `requiresPhone` reflects what each gateway's own hosted checkout requires:
//   - Flutterwave Standard rejects the request if `phonenumber` isn't sent
//     up front (the user never gets a chance to type it on Flutterwave's
//     page), so we collect it client-side before redirecting.
//   - Paystack / Seerbit / Monnify accept an empty phone — Paystack and
//     Seerbit will collect it on their own checkout page if they need it,
//     Monnify treats it as optional in most configurations.
//
// NB: The CRM body always includes `phone_number`. If the CRM backend turns
// out to reject empty phone for any gateway, the console log in
// purchasePackage() will surface the 400 and we revisit this.
const PAYMENT_GATEWAYS = [
  // Temporarily hidden per client — Seerbit only for now
  // { id: "flutterwave", label: "Flutterwave", requiresPhone: true },
  { id: "seerbit", label: "Seerbit", requiresPhone: false },
  // { id: "paystack", label: "Paystack", requiresPhone: false },
  // { id: "monnify", label: "Monnify", requiresPhone: false },
];

const GATEWAY_BY_ID = PAYMENT_GATEWAYS.reduce((acc, gw) => {
  acc[gw.id] = gw;
  return acc;
}, {});

const GatewayLogo = ({ id }) => {
  switch (id) {
    case "flutterwave":
      return (
        <svg viewBox="0 0 140 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <g transform="translate(6 6)">
            <circle cx="16" cy="14" r="12" fill="#F5A623" opacity="0.95" />
            <circle cx="26" cy="22" r="12" fill="#F5822A" opacity="0.95" />
            <circle cx="22" cy="10" r="9" fill="#22B573" opacity="0.95" />
          </g>
          <text x="48" y="32" fontFamily="Poppins, sans-serif" fontWeight="600" fontSize="16" fill="#0C2545">
            flutterwave
          </text>
        </svg>
      );
    case "seerbit":
      return (
        <svg viewBox="0 0 140 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <text
            x="70"
            y="32"
            textAnchor="middle"
            fontFamily="Poppins, sans-serif"
            fontWeight="800"
            fontSize="22"
            letterSpacing="2"
            fill="#E62B2B"
          >
            SEERBIT
          </text>
        </svg>
      );
    case "paystack":
      return (
        <svg viewBox="0 0 140 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <g transform="translate(10 14)">
            <rect x="0" y="0" width="14" height="3.5" rx="1.5" fill="#0BA4E0" />
            <rect x="0" y="6" width="14" height="3.5" rx="1.5" fill="#0BA4E0" />
            <rect x="0" y="12" width="9" height="3.5" rx="1.5" fill="#0BA4E0" />
            <rect x="0" y="18" width="14" height="3.5" rx="1.5" fill="#0BA4E0" />
          </g>
          <text x="34" y="32" fontFamily="Poppins, sans-serif" fontWeight="700" fontSize="18" fill="#0B2240">
            paystack
          </text>
        </svg>
      );
    case "monnify":
      return (
        <svg viewBox="0 0 140 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <defs>
            <linearGradient id="mono-grad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#1FC8DB" />
              <stop offset="100%" stopColor="#2CB67D" />
            </linearGradient>
          </defs>
          <g transform="translate(10 12)" fill="url(#mono-grad)">
            <path d="M2 22 L2 4 L8 4 L13 14 L18 4 L24 4 L24 22 L19 22 L19 12 L14.5 21 L11.5 21 L7 12 L7 22 Z" />
          </g>
          <text x="40" y="32" fontFamily="Poppins, sans-serif" fontWeight="700" fontSize="18" fill="#1F2A37">
            monnify
          </text>
        </svg>
      );
    default:
      return null;
  }
};

const PickPlanPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [product, setProduct] = useState(null);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [toast, setToast] = useState({ visible: false, message: "", type: "success" });

  // CRM purchase form state
  const [showForm, setShowForm] = useState(false);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [formError, setFormError] = useState("");
  // Steps inside the gateway modal:
  //   - "pick"       → 2x2 gateway grid
  //   - "phone"      → single phone input (Flutterwave path only, when phone missing)
  //   - "processing" → spinner while the CRM purchase call is in flight
  const [modalStep, setModalStep] = useState("pick");
  const [pendingGateway, setPendingGateway] = useState(null);
  const [processingGateway, setProcessingGateway] = useState(null);

  // Context from MovieDetailsPage / DiscoveryCategoryPage for post-purchase routing
  const returnMovieUid = location.state?.movieUid ?? null;
  const returnMovieId = location.state?.movieId ?? null;
  const returnPath = location.state?.returnPath ?? null;
  // Content type ("movie" | "series") of what the user was trying to play, so
  // the post-payment redirect can re-open the player with the correct ?type=.
  // Episodes played as type=movie hang on "Loading…" forever.
  const returnType = location.state?.returnType ?? null;

  // Fetch product detail + user price options
  useEffect(() => {
    if (!productId) return;

    const loadProduct = async () => {
      setLoading(true);
      console.log("[PickPlanPage] Loading product:", productId);

      // 1. Fetch product detail (name, description, options)
      const prod = await fetchTvStoreProduct(productId);
      if (prod) {
        setProduct(prod);
        console.log("[PickPlanPage] Product:", prod.name, "options:", prod.tv_store_options?.length);
      }

      // 2. Fetch user TV Store for display names + prices (CRM purchase only needs product_id, no package resolution)
      try {
        const accessToken = localStorage.getItem("mango_access_token");
        const userId = localStorage.getItem("mango_user_id");

        const res = await fetch(`${getClientApiUrl()}/api/client/v1/9mobile/users/${userId}/tv_store`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const data = await res.json();

        let priceOptions = null;
        if (data.status === "ok") {
          const userProd = data.data?.find((p) => String(p.id) === String(productId));
          if (userProd?.price_options?.length) {
            priceOptions = userProd.price_options;
            console.log("[PickPlanPage] Plans from v1:", priceOptions.length);
          }
        }

        // Fallback: if not in v1 (e.g. already-owned SVOD bundles like the
        // freetowatch product that grants podcast access), pull plans from v2.
        if (!priceOptions) {
          console.log("[PickPlanPage] Product not in v1, falling back to v2");
          const res2 = await fetch(`${getClientApiUrl()}/api/client/v2/9mobile/users/${userId}/tv_store`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          });
          const data2 = await res2.json();
          const v2Prod = (data2.data || {})[String(productId)];
          if (v2Prod?.price_options?.length) {
            priceOptions = v2Prod.price_options;
            console.log("[PickPlanPage] Plans from v2:", priceOptions.length);
          } else {
            console.warn("[PickPlanPage] No price_options found in v1 or v2 for product:", productId);
          }
        }

        if (priceOptions) {
          const planList = priceOptions.map((po, i) => ({
            uid: po.uid,
            name: po.name || po.uid,
            price: po.activation_price,
            renewalPrice: po.renewal_price,
            purchased: po.purchased_at || po.purchased,
            isBestValue: i === 0,
          }));
          setPlans(planList);
          setSelectedPlan(planList[0]?.uid || null);
          console.log("[PickPlanPage] Plans:", planList.map((p) => `${p.name}:${p.price}`).join(", "));
        }
      } catch (e) {
        console.error("[PickPlanPage] Error fetching user TV store:", e.message);
      }

      setLoading(false);
    };

    loadProduct();
  }, [productId]);

  const selectedPlanObj = plans.find((p) => p.uid === selectedPlan);
  const planDisplayName = selectedPlanObj?.name
    ? selectedPlanObj.name.charAt(0).toUpperCase() + selectedPlanObj.name.slice(1)
    : "Plan";

  // Open the Payment Gateway picker. Email/phone are pulled silently from
  // localStorage (email is set during onboarding; phone is set after the
  // user's first successful purchase). Phone may be empty for first-time
  // payers — the gateway-aware flow handles that below.
  const openPurchaseForm = () => {
    if (!selectedPlan || !productId || purchasing) return;
    setEmail(
      localStorage.getItem("mango_email") ||
      localStorage.getItem("mango_username") ||
      ""
    );
    setPhone(localStorage.getItem("mango_user_phone") || "");
    setFormError("");
    setModalStep("pick");
    setPendingGateway(null);
    setShowForm(true);
  };

  const closePurchaseForm = () => {
    if (purchasing) return;
    setShowForm(false);
    setFormError("");
    setModalStep("pick");
    setPendingGateway(null);
    setProcessingGateway(null);
  };

  // Gateway click — branches on whether that gateway needs phone up front.
  // - Flutterwave + phone missing → show phone input step, defer purchase
  // - All others (and Flutterwave when phone is already stored) → fire purchase
  const handleSelectGateway = (gatewayId) => {
    if (purchasing) return;
    const gateway = GATEWAY_BY_ID[gatewayId];
    console.log("[PickPlanPage] Gateway selected:", gatewayId, "requiresPhone:", gateway?.requiresPhone);

    if (gateway?.requiresPhone && !phone.trim()) {
      setPendingGateway(gatewayId);
      setFormError("");
      setModalStep("phone");
      return;
    }
    handleStartPlan(gatewayId);
  };

  // Submit handler for the phone step (Flutterwave fallback).
  const handleSubmitPhone = () => {
    if (purchasing) return;
    if (!phone.trim()) {
      setFormError("Please enter your phone number to continue with Flutterwave.");
      return;
    }
    setFormError("");
    handleStartPlan(pendingGateway || "flutterwave");
  };

  const handleStartPlan = async (gatewayId) => {
    if (!selectedPlan || !productId || purchasing) return;
    if (!email.trim()) {
      setFormError("We couldn't find your email. Please re-login and try again.");
      return;
    }

    // Phone may legitimately be empty for Paystack/Seerbit/Monnify — those
    // gateways either don't need it or collect it on their own hosted page.
    const phoneToSend = phone.trim();

    console.log("[PickPlanPage] Submitting CRM purchase:", {
      productId,
      priceOption: selectedPlan,
      gateway: gatewayId,
      email: email.trim(),
      phone: phoneToSend || "(empty — gateway will collect or not require)",
    });
    setFormError("");
    setProcessingGateway(gatewayId);
    setModalStep("processing");
    setPurchasing(true);

    // GloTV CRM path — POST /cm/api/purchase/ via crmRequest helper.
    // Returns redirect_url for hosted payment gateway.
    const result = await purchasePackage(productId, email.trim(), phoneToSend, selectedPlan, gatewayId);

    if (result.success) {
      if (result.redirectUrl) {
        // Stash the in-flight purchase context so /payment-callback can route
        // the user back to the movie / details page they came from once the
        // gateway redirects them back to our app.
        try {
          localStorage.setItem(
            "mango_pending_purchase",
            JSON.stringify({
              productId,
              productName: product?.name || null,
              plan: selectedPlan,
              planLabel: `${planDisplayName} Pass`,
              gateway: gatewayId || null,
              returnMovieUid,
              returnMovieId,
              returnPath,
              returnType,
              startedAt: Date.now(),
            })
          );
        } catch (e) {
          console.warn("[PickPlanPage] Could not stash pending purchase:", e.message);
        }

        setToast({ visible: true, message: "Redirecting to payment…", type: "info" });
        setTimeout(() => {
          window.location.href = result.redirectUrl;
        }, 600);
        return;
      }

      // No redirect — instant activation
      setToast({
        visible: true,
        message: `${planDisplayName} Pass activated`,
        type: "success",
      });
      setShowForm(false);
      setTimeout(() => {
        if (returnMovieUid) {
          navigate(`/player/${returnMovieUid}?type=${returnType || "movie"}`, { state: { movieId: returnMovieId } });
        } else if (returnMovieId) {
          navigate(`/details/${returnMovieId}`);
        } else {
          navigate(returnPath || "/rent");
        }
      }, 1200);
    } else {
      setToast({
        visible: true,
        message: result.error || "Purchase failed. Please try again.",
        type: "error",
      });
      setPurchasing(false);
      // Send the user back to the gateway picker so they can try a different
      // provider or retry. Keep `pendingGateway` so the phone step preserves
      // its context if it was the Flutterwave path.
      setProcessingGateway(null);
      setModalStep("pick");
    }
  };

  // Auto-open the gateway modal when there is only one plan — no need to
  // make the user confirm something they have no choice about.
  useEffect(() => {
    if (loading || plans.length !== 1) return;
    setEmail(
      localStorage.getItem("mango_email") ||
      localStorage.getItem("mango_username") ||
      ""
    );
    setPhone(localStorage.getItem("mango_user_phone") || "");
    setFormError("");
    setModalStep("pick");
    setPendingGateway(null);
    setShowForm(true);
  }, [loading, plans.length]);

  const hideToast = () => setToast((t) => ({ ...t, visible: false }));

  console.log("[PickPlanPage] rendering, product:", product?.name, "plans:", plans.length, "selected:", selectedPlan);

  return (
    <div className="pick-plan">
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.visible}
        onClose={hideToast}
      />
      <Navbar activeTab="Discover" />

      <div className="pick-plan__body">
        {loading ? (
          <div className="pick-plan__loader">
            <div className="pick-plan__loader-ring" />
          </div>
        ) : (
          <div className="pick-plan__content">
            {/* Left — Heading + Description */}
            <div className="pick-plan__left">
              <h1 className="pick-plan__title">Pick a Plan</h1>
              <p className="pick-plan__desc">
                Choose the plan that fits your vibe and start streaming your favorites your way.
                Whether you're here for blockbuster movies, binge-worthy series, or exclusive originals,
                we've got a subscription tailored just for you.
              </p>
              <p className="pick-plan__tagline">
                Pick your plan, press play, and let the story begin.
              </p>
            </div>

            {/* Right — Plan Cards + CTA */}
            <div className="pick-plan__right">
              {plans.length === 0 && !loading && (
                <p className="pick-plan__empty">No plans available for this product</p>
              )}

              {plans.map((plan) => (
                <button
                  key={plan.uid}
                  className={`pick-plan__card ${selectedPlan === plan.uid ? "pick-plan__card--selected" : ""}`}
                  onClick={() => setSelectedPlan(plan.uid)}
                >
                  <div className="pick-plan__card-check">
                    {selectedPlan === plan.uid ? (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="11" stroke="white" strokeWidth="1.5" fill="white" />
                        <path d="M8 12L11 15L16 9" stroke="#0a0a0a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    ) : (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="11" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" />
                      </svg>
                    )}
                  </div>
                  <span className="pick-plan__card-name">{plan.name} Pass</span>
                  {plan.isBestValue && (
                    <span className="pick-plan__card-badge">Best Value</span>
                  )}
                </button>
              ))}

              {plans.length > 0 && (
                <button
                  className="pick-plan__cta"
                  onClick={openPurchaseForm}
                  disabled={purchasing}
                >
                  {purchasing ? "Processing…" : `Start ${planDisplayName} Pass`}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/*
        Legacy CRM email/phone purchase form — superseded by the Payment Gateway
        selector below. Kept commented for reference / quick rollback.

        {showForm && (
          <div className="pick-plan__overlay" onClick={closePurchaseForm}>
            <div className="pick-plan__form" onClick={(e) => e.stopPropagation()}>
              <h3 className="pick-plan__form-title">Complete your purchase</h3>
              <p className="pick-plan__form-subtitle">
                {product?.name} — {planDisplayName} Pass ({selectedPlanObj?.price ? `₦${parseFloat(selectedPlanObj.price).toLocaleString()}` : ""})
              </p>

              <div className="pick-plan__form-fields">
                <TextInput
                  label="Email Address"
                  value={email}
                  onChange={setEmail}
                  placeholder="you@example.com"
                  type="email"
                />
                <TextInput
                  label="Phone Number"
                  value={phone}
                  onChange={setPhone}
                  placeholder="Enter your phone number"
                  type="tel"
                />
              </div>

              {formError && <p className="pick-plan__form-error">{formError}</p>}

              <div className="pick-plan__form-actions">
                <button
                  className="pick-plan__cta"
                  onClick={handleStartPlan}
                  disabled={purchasing}
                >
                  {purchasing ? "Processing…" : "Proceed to Payment"}
                </button>
                <button
                  className="pick-plan__form-cancel"
                  onClick={closePurchaseForm}
                  disabled={purchasing}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      */}

      {showForm && (
        <div className="pick-plan__gateway-overlay" onClick={closePurchaseForm}>
          <div className="pick-plan__gateway" onClick={(e) => e.stopPropagation()}>
            {modalStep === "processing" && (
              <div className="pick-plan__gateway-processing">
                <div className="pick-plan__gateway-spinner" aria-hidden="true" />
                <h3 className="pick-plan__gateway-title">
                  Connecting to {GATEWAY_BY_ID[processingGateway]?.label || "payment gateway"}…
                </h3>
                <p className="pick-plan__gateway-subtitle">
                  Don't close this window. We'll redirect you to a secure payment page in a moment.
                </p>
              </div>
            )}

            {modalStep === "pick" && (
              <>
                <div className="pick-plan__gateway-header">
                  <h3 className="pick-plan__gateway-title">Select Payment Gateway</h3>
                  <p className="pick-plan__gateway-subtitle">
                    Choose your payment gateway to proceed
                  </p>
                </div>

                <div className={`pick-plan__gateway-grid${PAYMENT_GATEWAYS.length === 1 ? " pick-plan__gateway-grid--single" : ""}`}>
                  {PAYMENT_GATEWAYS.map((gw) => (
                    <button
                      key={gw.id}
                      type="button"
                      className="pick-plan__gateway-card"
                      onClick={() => handleSelectGateway(gw.id)}
                      disabled={purchasing}
                      aria-label={`Pay with ${gw.label}`}
                    >
                      <GatewayLogo id={gw.id} />
                    </button>
                  ))}
                </div>

                {formError && <p className="pick-plan__gateway-error">{formError}</p>}
              </>
            )}

            {modalStep === "phone" && (
              <>
                <div className="pick-plan__gateway-header">
                  <h3 className="pick-plan__gateway-title">Enter your phone number</h3>
                  <p className="pick-plan__gateway-subtitle">
                    {GATEWAY_BY_ID[pendingGateway]?.label || "This gateway"} needs your
                    phone number to complete the payment.
                  </p>
                </div>

                <div className="pick-plan__gateway-phone">
                  <label htmlFor="pick-plan-phone" className="pick-plan__gateway-phone-label">
                    Phone Number
                  </label>
                  <input
                    id="pick-plan-phone"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    className="pick-plan__gateway-phone-input"
                    placeholder="e.g. 08012345678"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    disabled={purchasing}
                  />
                </div>

                {formError && <p className="pick-plan__gateway-error">{formError}</p>}

                <div className="pick-plan__gateway-actions">
                  <button
                    type="button"
                    className="pick-plan__gateway-submit"
                    onClick={handleSubmitPhone}
                    disabled={purchasing}
                  >
                    {purchasing ? "Processing…" : "Continue to payment"}
                  </button>
                  <button
                    type="button"
                    className="pick-plan__gateway-back"
                    onClick={() => {
                      if (purchasing) return;
                      setFormError("");
                      setModalStep("pick");
                    }}
                    disabled={purchasing}
                  >
                    Back
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default PickPlanPage;
